import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { CloseIcon } from "../../../img/svg/adminIcons";

type ModalSize = "sm" | "md" | "lg" | "xl";

const sizes: { [key in ModalSize]: string } = {
    sm: "md:max-w-md",
    md: "md:max-w-2xl",
    lg: "md:max-w-4xl",
    xl: "md:max-w-6xl",
}

// pilha dos modais abertos: só o do topo responde ao Esc, e o scroll da página
// só volta quando o último modal fecha (há modais abertos sobre outros modais)
const openModals: string[] = [];

export default function AdminModal({
    close,
    children,
    title,
    subtitle,
    size = "md",
    footer,
    tabs
}: {
    close: () => void;
    children: React.ReactNode;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    size?: ModalSize;
    footer?: React.ReactNode;
    tabs?: React.ReactNode;
}) {

    const id = useId();
    const closeRef = useRef(close);
    closeRef.current = close;

    useEffect(() => {
        openModals.push(id);
        document.body.style.overflow = "hidden";

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && openModals[openModals.length - 1] === id) {
                closeRef.current();
            }
        }
        window.addEventListener("keydown", onKeyDown);

        return () => {
            openModals.splice(openModals.indexOf(id), 1);
            if (!openModals.length) {
                document.body.style.overflow = "";
            }
            window.removeEventListener("keydown", onKeyDown);
        }
    }, [id]);

    // renderizado no body: evita que o modal fique preso dentro de containers com transform/overflow.
    // o stopPropagation mantém o comportamento antigo de cliques internos não chegarem aos pais no React.
    return createPortal(
        <div onClick={e => e.stopPropagation()} className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-6 font-lato text-gray-900" role="dialog" aria-modal="true">
            <div onClick={close} className="absolute inset-0 bg-gray-950/60 animate-admin-fade motion-reduce:animate-none" />
            {/* com abas, a altura fica fixa para o modal não "pular" ao trocar de aba */}
            <div className={`relative flex flex-col w-full max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] md:max-h-[88vh] bg-white rounded-t-2xl md:rounded-2xl shadow-2xl animate-admin-sheet md:animate-admin-pop motion-reduce:animate-none ${tabs ? "h-[92vh] supports-[height:100dvh]:h-[92dvh] md:h-[min(720px,88vh)]" : ""} ${sizes[size]}`}>
                <div className="md:hidden flex justify-center pt-2">
                    <span className="h-1 w-10 rounded-full bg-gray-300" />
                </div>
                <div className="flex items-start justify-between gap-4 px-5 md:px-6 pt-3 md:pt-5 pb-4 border-b border-gray-200">
                    <div className="min-w-0">
                        {
                            title ?
                                <h2 className="text-lg font-bold leading-snug text-gray-900 break-words">{title}</h2>
                                : null
                        }
                        {
                            subtitle ?
                                <div className="text-sm text-gray-500 mt-0.5 break-words">{subtitle}</div>
                                : null
                        }
                    </div>
                    <button type="button" onClick={close} aria-label="Fechar" className="-mr-2 -mt-1 p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary">
                        <CloseIcon width="20px" />
                    </button>
                </div>
                {
                    tabs ?
                        <div className="px-5 md:px-6 border-b border-gray-200">{tabs}</div>
                        : null
                }
                <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-6 py-5">
                    {children}
                </div>
                {
                    footer ?
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 md:px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-4 border-t border-gray-200 bg-gray-50 md:rounded-b-2xl">
                            {footer}
                        </div>
                        : <div className="md:hidden pb-[env(safe-area-inset-bottom)]" />
                }
            </div>
        </div>,
        document.body
    )
}
